import { normalize } from "normalizr";
import { change, getFormValues, reset } from "redux-form";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { faqAskQuestionUrl } from "@/mk2/apps/faq/urls";
import {
    POST_CREATE_EDIT_FORM_NAME,
    PostFormData,
} from "@/mk2/apps/forum/components/PostCreateEditForm";
import { UNCATEGORIZED_TOPIC_ID } from "@/mk2/apps/forum/constants";
import {
    POST_CREATE_FETCH_TRIGGER,
    POST_CREATE_PENDING_ACTION_TRIGGER,
    POST_CREATE_SET_ANONYMOUS_MODE,
    POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION,
    POST_CREATE_TRIGGER,
    PostCreateFetchNormalizedResponse,
    PostCreateFetchSuccessAction,
    PostCreateNormalizedResponse,
    PostCreatePendingActionTriggerAction,
    PostCreateSetAnonymousModeAction,
    PostCreateSetWantsMkToFoundDiscussionAction,
    PostCreateTriggerAction,
    postCreateApi,
    postCreateFetchApi,
} from "@/mk2/apps/forum/containers/PostCreate/PostCreate.actions";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import { PostEntity, PostSchema, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import { forumPostDetailUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { gtmEvent } from "@/mk2/apps/users/utils";
import { FormPageType } from "@/mk2/constants/enums";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { mkReduxFormCancelPage, mkReduxFormOpenPage } from "@/mk2/helpers/form.actions";
import { PhotoUploadStatus } from "@/mk2/helpers/form.reducers";
import { getSessionStorage } from "@/mk2/helpers/storages";
import { showSuccessToast } from "@/mk2/helpers/toasts";
import { tupdate } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { checkIfUserIsLoggedIn } from "@/mk2/sagas/auth";
import { HistoryAction, pushInPWA } from "@/mk2/services/browserHistory";
import {
    FORUM_GROWL_YOUR_USERNAME_WILL_BE_SHOWN,
    FORUM_GROWL_YOUR_USERNAME_WONT_BE_SHOWN,
    FORUM_POST_DRAFT_FOR_TICKET_INTRO,
} from "@/mk/autogenerated/translation";
import {
    forum_api_get_topic_info_url,
    forum_api_post_create_url,
    pending_action_create_url,
} from "@/mk/urls/functions";

const logger = getLogger("forum/PostCreate.sagas");

interface ApiResponsePostCreateFetch {
    body: {
        topic: TopicEntity;
    };
}

function* fetch({
    topicSlug,
    xhr,
    location,
    routeNavigate,
}: PostCreateFetchSuccessAction & XHRAction & HistoryAction) {
    yield put(postCreateFetchApi.request(topicSlug));

    try {
        const response: ApiResponsePostCreateFetch = yield call(() =>
            xhr.get(forum_api_get_topic_info_url(), { topicSlug }),
        );

        const normalizedResponse: PostCreateFetchNormalizedResponse = normalize(response.body, {
            topic: TopicSchema,
        });
        yield put(postCreateFetchApi.success(topicSlug, normalizedResponse));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(postCreateFetchApi.failure(topicSlug, normalizeError(error)));
    }
}

function* setWantsMkToFoundDiscussion({
    location,
    routeNavigate,
}: PostCreateSetWantsMkToFoundDiscussionAction & HistoryAction) {
    const formValues: PostFormData = yield select(getFormValues(POST_CREATE_EDIT_FORM_NAME));
    getSessionStorage().setItem(
        "forumPostDraftForTicket",
        JSON.stringify({
            intro: FORUM_POST_DRAFT_FOR_TICKET_INTRO,
            title: formValues?.title || "",
            perex: formValues?.perex || "",
        }),
    );
    pushInPWA(routeNavigate, { pathname: faqAskQuestionUrl, state: { formIgnorePrompt: true } });
}

function* setAnonymousMode({ enabled }: PostCreateSetAnonymousModeAction) {
    yield put(mkReduxFormCancelPage(POST_CREATE_EDIT_FORM_NAME));
    yield put(
        change(POST_CREATE_EDIT_FORM_NAME, "anonymousMode", enabled ? "enabled" : "disabled"),
    );
    yield put(
        mkReduxFormOpenPage(POST_CREATE_EDIT_FORM_NAME, FormPageType.AnonymousModeExplanation),
    );

    yield showSuccessToast(
        enabled ? FORUM_GROWL_YOUR_USERNAME_WONT_BE_SHOWN : FORUM_GROWL_YOUR_USERNAME_WILL_BE_SHOWN,
    );
}

interface ApiResponsePostCreate {
    body: {
        post: PostEntity;
        postIsRemovedFromFeeds: boolean;
        notificationsOn: number[];
    };
}

function* save({
    anonymousMode,
    postFd,
    topicSlug,
    xhr,
    location,
    routeNavigate,
}: PostCreateTriggerAction & XHRAction & HistoryAction) {
    yield put(postCreateApi.request(topicSlug, postFd));
    try {
        const response: ApiResponsePostCreate = yield call(() =>
            xhr.post(forum_api_post_create_url(), {
                topicSlug,
                anonymousMode,
                postData: tupdate(postFd, {
                    photos: postFd.photos
                        ? postFd.photos.filter(
                              (photo) => photo.status === PhotoUploadStatus.SUCCESS,
                          )
                        : [],
                }),
            }),
        );
        const normalizedResponse: PostCreateNormalizedResponse = normalize(response.body, {
            post: PostSchema,
        });

        const isUncategorized = postFd.topic && postFd.topic.id === UNCATEGORIZED_TOPIC_ID;
        const postIsRemovedFromFeeds = response.body.postIsRemovedFromFeeds;

        const gotoUrl = postIsRemovedFromFeeds
            ? url(forumPostDetailUrl, {
                  topicSlug: response.body.post.topic.slug,
                  postSlug: response.body.post.slug,
              })
            : isUncategorized
              ? forumUrl
              : topicUrl(response.body.post.topic);
        gtmEvent("event_forum_post_create");
        yield handleXHRFormSuccessSaga(POST_CREATE_EDIT_FORM_NAME, routeNavigate, gotoUrl);
        yield put(
            postCreateApi.success(
                topicSlug,
                postFd,
                response.body.notificationsOn,
                normalizedResponse,
            ),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(POST_CREATE_EDIT_FORM_NAME, error as XHRFormError, logger);
        yield put(postCreateApi.failure(topicSlug, postFd, normalizeError(error)));
    }
}

function* pendingActionCreate({
    topicSlug,
    postFd,
    xhr,
    location,
    routeNavigate,
}: PostCreatePendingActionTriggerAction & XHRAction & HistoryAction) {
    try {
        const response = yield call(() =>
            xhr.post(pending_action_create_url(), {
                actionType: "forum_discussion_create",
                payload: {
                    topicSlug,
                    title: postFd.title,
                    perex: postFd.perex || "",
                },
            }),
        );
        const token = response.body.token;
        yield put(reset(POST_CREATE_EDIT_FORM_NAME));
        const publishUrl = `/pending-action/${token}/publish/`;
        yield call(
            checkIfUserIsLoggedIn,
            routeNavigate,
            location,
            publishUrl,
            "pa-forum-discussion",
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(POST_CREATE_EDIT_FORM_NAME, error as XHRFormError, logger);
    }
}

export default function* root() {
    yield all([
        takeLatest(POST_CREATE_FETCH_TRIGGER, fetch),
        takeEvery(POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION, setWantsMkToFoundDiscussion),
        takeEvery(POST_CREATE_SET_ANONYMOUS_MODE, setAnonymousMode),
        takeEvery(POST_CREATE_TRIGGER, save),
        takeEvery(POST_CREATE_PENDING_ACTION_TRIGGER, pendingActionCreate),
    ]);
}
